import {
  INVOICE_MATCH_RESULT_LABELS,
  INVOICE_MATCH_STATUS_LABELS,
  INVOICE_MATCH_STATUSES,
  type InvoiceMatch,
  type InvoiceMatchCandidates,
  invoiceMatchStatusSchema,
} from "@station/contracts";
import { newIdempotencyKey } from "@station/api-client";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FileDiff, Link2 } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CounterpartyIdCombobox } from "../../../../features/counterparties/counterparty-combobox.js";
import { Th } from "../../../../features/fuel/shared.js";
import { moneyExport } from "../../../../features/market/market-values.js";
import {
  matchBody,
  selectionPreview,
  suggestedReceiptIds,
} from "../../../../features/market/match-values.js";
import {
  invoiceMatchesQuery,
  type InvoiceMatchFilters,
  useInvoiceMatches,
  useMatchCandidates,
} from "../../../../features/market/purchasing-queries.js";
import { useMarketPermissions, useRefreshMarket } from "../../../../features/market/queries.js";
import {
  InvoiceMatchStatusBadge,
  MarketPage,
  marketSearchShape,
  TableFrame,
} from "../../../../features/market/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatCount, formatDate, formatDateTime } from "../../../../lib/format.js";

const TABS = ["matches", "new"] as const;
type MatchTab = (typeof TABS)[number];

const searchSchema = z.object({
  ...marketSearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
  status: invoiceMatchStatusSchema.optional().catch(undefined),
  supplier: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/eslestirme/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: InvoiceMatchesRoute,
});

/**
 * Fatura eşleştirme — üçlü eşleştirme (Prompt 16 — web, aşama B; ADR 0016 §9–10). İki sekme: EŞLEŞMELER (liste,
 * fark raporuna bağlantı) ve YENİ EŞLEŞTİRME (tedarikçi seçilir → eşleşmemiş kesinleşmiş alış faturaları ve kayıtlı,
 * eşleşmemiş mal kabulleri → fatura + kabul(ler) → eşleştir). Tam eşleşmede kesin maliyet HEMEN uygulanır; farklı
 * eşleşme onay ister. Liste `market.read`; aday ve eşleştirme `market.invoice.match`.
 */
function InvoiceMatchesRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketInvoiceMatches")}
      description={t("market.matches.description")}
      breadcrumb={[{ label: t("menu.marketInvoiceMatches") }]}
    >
      {({ companyId }) => <InvoiceMatchesBody companyId={companyId} />}
    </MarketPage>
  );
}

function InvoiceMatchesBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const tab: MatchTab = permissions.canMatchInvoices ? (search.tab ?? "matches") : "matches";

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const supplierField = (
    <FormField label={t("market.orders.fields.supplier")} className="w-72">
      {(control) => (
        <CounterpartyIdCombobox
          {...control}
          companyId={companyId}
          counterpartyId={search.supplier ?? null}
          placeholder={
            tab === "new" ? t("market.matches.chooseSupplier") : t("market.suggestions.allSuppliers")
          }
          onChange={(supplierId) => setSearch({ supplier: supplierId ?? undefined })}
        />
      )}
    </FormField>
  );

  if (!permissions.canMatchInvoices)
    return <MatchList companyId={companyId} supplierField={supplierField} onFilter={setSearch} />;

  return (
    <Tabs className="grid gap-4" value={tab} onValueChange={(next) => setSearch({ tab: next })}>
      <TabsList aria-label={t("market.matches.tabsLabel")}>
        {TABS.map((value) => (
          <TabsTrigger key={value} value={value}>
            {t(`market.matches.tab.${value}`)}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value={tab} className="grid gap-4 pt-0">
        {tab === "matches" ? (
          <MatchList companyId={companyId} supplierField={supplierField} onFilter={setSearch} />
        ) : (
          <>
            <Card className="flex flex-wrap items-end gap-3 p-4">{supplierField}</Card>
            {search.supplier ? (
              <Candidates key={search.supplier} companyId={companyId} supplierId={search.supplier} />
            ) : (
              <EmptyState
                headingLevel={2}
                icon={<Link2 />}
                title={t("market.matches.pickSupplierTitle")}
                description={t("market.matches.pickSupplierDescription")}
              />
            )}
          </>
        )}
      </TabsContent>
    </Tabs>
  );
}

// ── Eşleşme listesi ──────────────────────────────────────────────────────────
function MatchList({
  companyId,
  supplierField,
  onFilter,
}: {
  companyId: string;
  supplierField: ReactElement;
  onFilter: (patch: Record<string, unknown>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const filters: InvoiceMatchFilters = { supplierId: search.supplier, status: search.status };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useInvoiceMatches(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<InvoiceMatch>[]>(
    () => [
      {
        id: "invoice",
        header: t("market.matches.fields.invoice"),
        hideable: false,
        exportValue: (row) => row.invoiceNo,
        cell: (row) => (
          <Link
            to="/market/eslestirme/$id"
            params={{ id: row.id }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span className="font-mono whitespace-nowrap">
              {row.invoiceNo ?? t("market.matches.noInvoiceNo")}
            </span>
            <span className="text-xs text-muted-foreground">{formatDate(row.invoiceIssueDate)}</span>
          </Link>
        ),
      },
      {
        id: "supplier",
        header: t("market.orders.fields.supplier"),
        exportValue: (row) => row.supplierName,
        cell: (row) => row.supplierName,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => INVOICE_MATCH_STATUS_LABELS[row.status],
        cell: (row) => (
          <span className="flex flex-wrap gap-1">
            <InvoiceMatchStatusBadge status={row.status} />
            {row.automatic ? <StatusBadge tone="neutral">{t("market.matches.system")}</StatusBadge> : null}
          </span>
        ),
      },
      {
        id: "result",
        header: t("market.matches.fields.result"),
        exportValue: (row) => INVOICE_MATCH_RESULT_LABELS[row.result],
        cell: (row) => INVOICE_MATCH_RESULT_LABELS[row.result],
      },
      {
        id: "receipts",
        header: t("market.matches.fields.receipts"),
        exportValue: (row) => row.receipts.map((receipt) => receipt.receiptNo).join(", "),
        cell: (row) => (
          <span className="font-mono text-xs">
            {row.receipts.map((receipt) => receipt.receiptNo).join(", ")}
          </span>
        ),
      },
      {
        id: "differenceLines",
        header: t("market.matches.fields.differenceLines"),
        align: "right",
        exportValue: (row) => row.differenceLineCount,
        cell: (row) => <span className="tabular">{formatCount(row.differenceLineCount)}</span>,
      },
      {
        id: "amountDifference",
        header: t("market.matches.fields.amountDifference"),
        align: "right",
        exportValue: (row) => moneyExport(row.amountDifference),
        cell: (row) => <MoneyText value={row.amountDifference} colorize />,
      },
      {
        id: "costAdjustment",
        header: t("market.matches.fields.costAdjustment"),
        align: "right",
        exportValue: (row) => moneyExport(row.costAdjustment),
        cell: (row) => <MoneyText value={row.costAdjustment} colorize />,
      },
      {
        id: "matchedAt",
        header: t("market.matches.fields.matchedAt"),
        defaultVisible: false,
        exportValue: (row) => row.matchedAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.matchedAt)}</span>,
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("market.matches.caption")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<FileDiff />}
          title={t("market.matches.emptyTitle")}
          description={t("market.matches.emptyDescription")}
        />
      }
      filters={
        <>
          <FormField label={t("common.status")} className="w-52">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) => onFilter({ status: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {INVOICE_MATCH_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {INVOICE_MATCH_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          {supplierField}
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
      onExport={async (visible) => {
        const rows = await fetchAllPages<InvoiceMatch>(
          async (cursor) =>
            (
              await api.GET("/market/invoice-matches", {
                params: { query: invoiceMatchesQuery(companyId, filters, cursor, 200) as never },
              })
            ).data!,
        );
        await exportRowsToExcel({
          fileName: "fatura-eslestirmeleri",
          sheetName: t("menu.marketInvoiceMatches"),
          columns: visible,
          rows,
        });
      }}
    />
  );
}

// ── Yeni eşleştirme: adaylar ─────────────────────────────────────────────────
type CandidateInvoice = InvoiceMatchCandidates["invoices"][number];

function Candidates({ companyId, supplierId }: { companyId: string; supplierId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshMarket();
  const candidates = useMatchCandidates(companyId, supplierId, true);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [receiptIds, setReceiptIds] = useState<string[]>([]);
  const [showProblems, setShowProblems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invoices = candidates.data?.invoices ?? [];
  const receipts = candidates.data?.receipts ?? [];
  const invoice = invoices.find((item) => item.invoiceId === invoiceId) ?? null;
  const selectedReceipts = receipts.filter((receipt) => receiptIds.includes(receipt.receiptId));
  const preview = selectionPreview(invoice, selectedReceipts);
  const { body, problems } = matchBody(invoiceId, receiptIds);

  const pickInvoice = (next: CandidateInvoice) => {
    setInvoiceId(next.invoiceId);
    // Faturaya bağlı irsaliye no'suyla aynı mal kabuller önerilir (kullanıcı değiştirebilir).
    const suggested = suggestedReceiptIds(next, receipts);
    if (suggested.length > 0) setReceiptIds(suggested);
  };

  const match = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/market/invoice-matches", {
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          body,
        })
      ).data!,
    onSuccess: async (created) => {
      await refresh();
      toast({
        tone: "success",
        title: t(`market.matches.done.${created.status === "PENDING_APPROVAL" ? "pending" : "matched"}`),
      });
      void navigate({ to: "/market/eslestirme/$id", params: { id: created.id } });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  if (candidates.error) return <Alert tone="danger">{errorMessage(candidates.error)}</Alert>;
  if (candidates.isPending) return <SkeletonRows rows={6} />;

  return (
    <>
      <Card className="grid gap-3 p-4" aria-labelledby="match-invoices-title">
        <h2 id="match-invoices-title" className="text-base font-semibold">
          {t("market.matches.invoicesTitle")}
        </h2>
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("market.matches.noInvoices")}</p>
        ) : (
          <TableFrame label={t("market.matches.invoicesTitle")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>
                  <span className="sr-only">{t("market.matches.selectInvoice")}</span>
                </Th>
                <Th>{t("market.matches.fields.invoice")}</Th>
                <Th>{t("market.matches.fields.despatchNos")}</Th>
                <Th align="right">{t("market.fields.lineCount")}</Th>
                <Th align="right">{t("market.matches.fields.invoiceNet")}</Th>
                <Th align="right">{t("market.matches.fields.payable")}</Th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((item) => (
                <tr key={item.invoiceId} className="border-t" data-testid="match-invoice">
                  <td className="px-3 py-2">
                    <input
                      type="radio"
                      name="match-invoice"
                      className="size-4 accent-primary"
                      checked={item.invoiceId === invoiceId}
                      aria-label={t("market.matches.selectInvoiceLabel", {
                        no: item.invoiceNo ?? t("market.matches.noInvoiceNo"),
                      })}
                      onChange={() => pickInvoice(item)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to="/faturalar/$id"
                      params={{ id: item.invoiceId }}
                      className="grid gap-0.5 underline underline-offset-4"
                    >
                      <span className="font-mono">{item.invoiceNo ?? t("market.matches.noInvoiceNo")}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(item.issueDate)}</span>
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {item.despatchNos.length > 0 ? item.despatchNos.join(", ") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular">{formatCount(item.lineCount)}</td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={item.netAmount} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={item.payableAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Card>

      <Card className="grid gap-3 p-4" aria-labelledby="match-receipts-title">
        <h2 id="match-receipts-title" className="text-base font-semibold">
          {t("market.matches.receiptsTitle")}
        </h2>
        {receipts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("market.matches.noReceipts")}</p>
        ) : (
          <TableFrame label={t("market.matches.receiptsTitle")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>
                  <span className="sr-only">{t("market.matches.selectReceipts")}</span>
                </Th>
                <Th>{t("market.receipts.fields.receiptNo")}</Th>
                <Th>{t("market.receipts.fields.despatchNo")}</Th>
                <Th>{t("market.fields.station")}</Th>
                <Th>{t("market.receipts.fields.order")}</Th>
                <Th align="right">{t("market.fields.lineCount")}</Th>
                <Th align="right">{t("market.receipts.fields.netAmount")}</Th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((receipt) => (
                <tr key={receipt.receiptId} className="border-t" data-testid="match-receipt">
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={receiptIds.includes(receipt.receiptId)}
                      aria-label={t("market.matches.selectReceiptLabel", { no: receipt.receiptNo })}
                      onChange={(event) =>
                        setReceiptIds((current) =>
                          event.target.checked
                            ? [...current, receipt.receiptId]
                            : current.filter((id) => id !== receipt.receiptId),
                        )
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to="/market/mal-kabul/$id"
                      params={{ id: receipt.receiptId }}
                      className="grid gap-0.5 underline underline-offset-4"
                    >
                      <span className="font-mono">{receipt.receiptNo}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(receipt.businessDate)}
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {receipt.despatchNo ?? "—"}
                    {receipt.despatchDate ? ` · ${formatDate(receipt.despatchDate)}` : ""}
                  </td>
                  <td className="px-3 py-2">{receipt.stationName}</td>
                  <td className="px-3 py-2 font-mono text-xs">{receipt.purchaseOrderNo ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular">{formatCount(receipt.lineCount)}</td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={receipt.netAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Card>

      <Card className="grid gap-3 p-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <dl className="grid gap-3 text-sm sm:grid-cols-3" data-testid="match-preview">
          <div className="grid gap-0.5">
            <dt className="text-xs text-muted-foreground">{t("market.matches.fields.invoiceNet")}</dt>
            <dd className="font-medium">{invoice ? <MoneyText value={invoice.netAmount} /> : "—"}</dd>
          </div>
          <div className="grid gap-0.5">
            <dt className="text-xs text-muted-foreground">{t("market.matches.receiptsTotal")}</dt>
            <dd className="font-medium">
              <MoneyText value={preview.receiptsTotal} />
            </dd>
          </div>
          <div className="grid gap-0.5">
            <dt className="text-xs text-muted-foreground">{t("market.matches.previewDifference")}</dt>
            <dd className="font-medium">
              {preview.difference === null ? "—" : <MoneyText value={preview.difference} colorize />}
            </dd>
          </div>
        </dl>
        <p className="text-xs text-muted-foreground">{t("market.matches.previewHint")}</p>
        {showProblems && problems.length > 0 ? (
          <Alert tone="warning">
            {problems.map((problem) => t(`market.matches.problem.${problem}`)).join(" ")}
          </Alert>
        ) : null}
        <div className="flex justify-end">
          <Button
            loading={match.isPending}
            onClick={() => {
              setShowProblems(true);
              setError(null);
              if (problems.length === 0) match.mutate();
            }}
          >
            <FileDiff aria-hidden="true" />
            {t("market.matches.match")}
          </Button>
        </div>
      </Card>
    </>
  );
}
